<template>
  <div class="flex flex-col h-full">
    <!-- Page nav -->
    <div class="p-4 border-b border-gray-200">
      <div class="sidebar-nav-title mb-2">
        Pages
      </div>
      <nav class="flex flex-col gap-0.5" aria-label="Site pages">
        <template v-for="group in pageGroups" :key="group.kind">
          <!-- Cloneable kind: header + clones + Add button -->
          <template v-if="group.cloneable">
            <div class="sidebar-nav-title mt-2 flex items-center gap-2">
              <span v-html="iconFor(group.kind)" class="h-3.5 w-3.5" />
              <span class="flex-1">{{ kindGroupLabel(group.kind) }}</span>
              <button
                type="button"
                class="btn btn-ghost btn-icon btn-xs"
                :title="`Add ${kindGroupLabel(group.kind)}`"
                @click="openAddForm(group)">
                +
              </button>
            </div>
            <button
              v-for="page in group.pages"
              :key="page.id"
              type="button"
              class="sidebar-nav-item"
              :aria-current="page.id === selectedPageId ? 'page' : undefined"
              :class="[rowClass(page), { active: page.id === selectedPageId }]"
              @click="$emit('select-page', page.id)">
              <span class="inline-flex h-1.5 w-1.5 rounded-full bg-gray-300 ml-1" aria-hidden="true" />
              <span class="flex-1 truncate">{{ labelFor(page) }}</span>
              <button
                v-if="isClone(page)"
                type="button"
                class="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-600"
                :title="`Remove ${page.title}`"
                @click.stop="$emit('delete-page', page.id)">
                ×
              </button>
            </button>
          </template>
          <!-- Singleton page -->
          <button
            v-else
            type="button"
            class="sidebar-nav-item"
            :aria-current="group.pages[0].id === selectedPageId ? 'page' : undefined"
            :class="[rowClass(group.pages[0]), { active: group.pages[0].id === selectedPageId }]"
            @click="$emit('select-page', group.pages[0].id)">
            <span
              aria-hidden="true"
              class="inline-flex h-5 w-5 items-center justify-center shrink-0 text-gray-400 group-hover:text-gray-600"
              v-html="iconFor(group.kind)" />
            <span class="flex-1 truncate">{{ labelFor(group.pages[0]) }}</span>
          </button>
        </template>
      </nav>

      <!-- Inline "Add page" form, shown when openAddForm is invoked -->
      <div v-if="addForm" class="mt-3 p-3 border border-gray-200 rounded-md bg-gray-50">
        <div class="text-xs font-medium text-gray-700 mb-2">
          Add a {{ kindGroupLabel(addForm.kind).toLowerCase() }}
        </div>
        <label v-if="addForm.needsService" class="block mb-2">
          <span class="form-label">Service</span>
          <select v-model="addForm.serviceId" class="form-select w-full mt-1">
            <option disabled value="">Choose a service…</option>
            <option v-for="s in availableServices(addForm)" :key="s.id" :value="s.id">{{ s.name }}</option>
          </select>
        </label>
        <label v-if="addForm.needsArea" class="block mb-2">
          <span class="form-label">Service area</span>
          <select v-model="addForm.serviceAreaId" class="form-select w-full mt-1">
            <option disabled value="">Choose a service area…</option>
            <option v-for="a in availableAreas(addForm)" :key="a.id" :value="a.id">{{ a.name }}</option>
          </select>
        </label>
        <div class="flex justify-end gap-2 mt-2">
          <button type="button" class="btn btn-secondary btn-sm" @click="addForm = null">
            Cancel
          </button>
          <button
            type="button"
            :disabled="!canSubmitAdd"
            class="btn btn-primary btn-sm"
            @click="submitAdd">
            Add page
          </button>
        </div>
        <p v-if="addForm.error" class="form-error mt-2">
          {{ addForm.error }}
        </p>
      </div>
    </div>

    <!-- Business identity -->
    <details class="border-b border-gray-200 group" open>
      <summary class="sidebar-nav-title px-4 py-3 cursor-pointer [list-style:none] flex items-center justify-between">
        Business identity
        <EditorIcon name="chevron-down" class="w-4 h-4 text-gray-400 group-open:rotate-180 transition-transform" />
      </summary>
      <form class="p-4 space-y-3" @submit.prevent="emitBusiness">
        <label class="block">
          <span class="form-label">Business name</span>
          <input v-model="biz.business_name" name="business_name" type="text" class="form-input w-full mt-1">
        </label>
        <label class="block">
          <span class="form-label">Phone</span>
          <input v-model="biz.phone" name="phone" type="text" class="form-input w-full mt-1">
        </label>
        <label class="block">
          <span class="form-label">Email</span>
          <input v-model="biz.email" name="email" type="text" class="form-input w-full mt-1">
        </label>
        <label class="block">
          <span class="form-label">Street address</span>
          <input v-model="biz.address_line_1" name="address_line_1" type="text" class="form-input w-full mt-1">
        </label>
        <label class="block">
          <span class="form-label">City</span>
          <CityAutocomplete v-model="biz.city" :map-key="googleMapsKey" @select="onCitySelect" />
        </label>
        <div class="grid grid-cols-2 gap-2">
          <label>
            <span class="form-label">State</span>
            <input v-model="biz.state" name="state" type="text" class="form-input w-full mt-1">
          </label>
          <label>
            <span class="form-label">ZIP</span>
            <input v-model="biz.zip_code" name="zip_code" type="text" class="form-input w-full mt-1">
          </label>
        </div>
        <div>
          <span class="form-label">Business hours</span>
          <BusinessHoursGrid v-model="biz.business_hours" class="mt-1" />
        </div>
        <button
          type="submit"
          :disabled="saving"
          class="btn btn-primary btn-sm btn-full">
          {{ saving ? 'Saving…' : 'Save business info' }}
        </button>
      </form>
    </details>

    <!-- Brand colors -->
    <details class="border-b border-gray-200 group">
      <summary class="sidebar-nav-title px-4 py-3 cursor-pointer [list-style:none] flex items-center justify-between">
        Brand colors
        <EditorIcon name="chevron-down" class="w-4 h-4 text-gray-400 group-open:rotate-180 transition-transform" />
      </summary>
      <form class="p-4 space-y-3" @submit.prevent="emitBrand">
        <label
          v-for="(defn, key) in colorTokens"
          :key="key"
          class="flex items-center justify-between gap-2">
          <span class="form-label">{{ defn.label || key }}</span>
          <input
            :value="palette[key] || defn.default"
            type="color"
            class="h-8 w-12 border-gray-300 rounded"
            @input="palette[key] = $event.target.value">
        </label>
        <button
          type="submit"
          :disabled="saving"
          class="btn btn-primary btn-sm btn-full">
          {{ saving ? 'Saving…' : 'Save colors' }}
        </button>
      </form>
    </details>

    <!-- Chat widget -->
    <details class="border-b border-gray-200 group">
      <summary class="sidebar-nav-title px-4 py-3 cursor-pointer [list-style:none] flex items-center justify-between">
        Chat widget
        <EditorIcon name="chevron-down" class="w-4 h-4 text-gray-400 group-open:rotate-180 transition-transform" />
      </summary>
      <form class="p-4 space-y-3" @submit.prevent="emitChat">
        <label class="flex items-center justify-between gap-2">
          <span class="text-sm text-gray-800">Enable chat widget</span>
          <input v-model="chat.enabled" type="checkbox" class="form-checkbox" :disabled="saving">
        </label>

        <div :class="{ 'opacity-50 pointer-events-none': !chat.enabled }" class="space-y-3">
          <label class="block">
            <span class="form-label">Greeting message</span>
            <textarea
              v-model="chat.greeting"
              rows="2"
              :placeholder="defaultGreeting"
              class="form-input w-full mt-1" />
            <span class="form-helper">Shown when a visitor starts a chat. Leave blank for the default.</span>
          </label>

          <label class="flex items-center justify-between gap-2">
            <span class="text-sm text-gray-800">Override widget color</span>
            <input v-model="chatCustomColor" type="checkbox" class="form-checkbox">
          </label>
          <label v-if="chatCustomColor" class="flex items-center justify-between gap-2">
            <span class="form-label">Widget color</span>
            <input
              :value="chat.color || chat.primary_default || '#111827'"
              type="color"
              class="h-8 w-12 border-gray-300 rounded"
              @input="chat.color = $event.target.value">
          </label>
          <p v-else class="form-helper">
            Using your brand’s primary color.
          </p>

          <label class="flex items-center justify-between gap-2">
            <span class="text-sm text-gray-800">Hide “Powered by Made.ly”</span>
            <input v-model="chat.hide_branding" type="checkbox" class="form-checkbox">
          </label>
        </div>

        <button
          type="submit"
          :disabled="saving"
          class="btn btn-primary btn-sm btn-full">
          {{ saving ? 'Saving…' : 'Save chat settings' }}
        </button>
      </form>
    </details>

    <!-- Blog -->
    <details class="border-b border-gray-200 group">
      <summary class="sidebar-nav-title px-4 py-3 cursor-pointer [list-style:none] flex items-center justify-between">
        Blog
        <EditorIcon name="chevron-down" class="w-4 h-4 text-gray-400 group-open:rotate-180 transition-transform" />
      </summary>
      <div class="p-4 space-y-3">
        <label class="flex items-center justify-between gap-2">
          <span class="text-sm text-gray-800">Enable blog</span>
          <input
            v-model="blogEnabled"
            type="checkbox"
            class="form-checkbox"
            :disabled="saving"
            @change="emitBlog">
        </label>
        <p class="text-xs text-gray-500">
          When on, the blog appears in your site navigation and renders at <code>/blog</code>. Manage posts from the
          <a v-if="blogPostsPath" :href="blogPostsPath" class="underline" target="_blank" rel="noopener">Blog posts</a>
          <span v-else>Blog posts</span>
          dashboard.
        </p>
      </div>
    </details>

    <!-- Header / Navigation -->
    <details class="border-b border-gray-200 group">
      <summary class="sidebar-nav-title px-4 py-3 cursor-pointer [list-style:none] flex items-center justify-between">
        Header &amp; Navigation
        <EditorIcon name="chevron-down" class="w-4 h-4 text-gray-400 group-open:rotate-180 transition-transform" />
      </summary>
      <div class="p-4">
        <HeaderEditor :header="headerInitial" :saving="saving" @save="(content) => $emit('save-header', content)" />
      </div>
    </details>

    <!-- Footer -->
    <details class="border-b border-gray-200 group">
      <summary class="sidebar-nav-title px-4 py-3 cursor-pointer [list-style:none] flex items-center justify-between">
        Footer
        <EditorIcon name="chevron-down" class="w-4 h-4 text-gray-400 group-open:rotate-180 transition-transform" />
      </summary>
      <div class="p-4">
        <FooterEditor :footer="footerInitial" :saving="saving" @save="(content) => $emit('save-footer', content)" />
      </div>
    </details>

    <!-- SEO & Social (site-wide defaults) -->
    <details class="border-b border-gray-200 group">
      <summary class="sidebar-nav-title px-4 py-3 cursor-pointer [list-style:none] flex items-center justify-between">
        SEO &amp; Social
        <EditorIcon name="chevron-down" class="w-4 h-4 text-gray-400 group-open:rotate-180 transition-transform" />
      </summary>
      <form class="p-4 space-y-3" @submit.prevent="emitSeo">
        <p class="form-helper">
          Defaults for every page. Individual pages can override these in their SEO panel.
        </p>

        <label class="block">
          <span class="form-label">Default meta description</span>
          <textarea
            v-model="seo.description"
            rows="2"
            placeholder="One or two sentences describing the business."
            class="form-input w-full mt-1" />
        </label>

        <div class="block">
          <span class="form-label">Default share image</span>
          <div class="flex items-center gap-2 mt-1">
            <input
              v-model="seo.default_og_image_url"
              type="text"
              placeholder="https://…/share.jpg"
              class="form-input flex-1">
            <button
              type="button"
              :disabled="seoImageUploading || !editorApi"
              class="btn btn-secondary btn-sm"
              @click="ogFileInput.click()">
              {{ seoImageUploading ? 'Uploading…' : 'Upload' }}
            </button>
            <input ref="ogFileInput" type="file" accept="image/*" class="hidden" @change="uploadSeoImage">
          </div>
          <span class="form-helper">Shown when pages are shared on social. Recommended 1200×630.</span>
        </div>

        <label class="block">
          <span class="form-label">Twitter / X handle</span>
          <input
            v-model="seo.twitter_handle"
            type="text"
            placeholder="@yourbusiness"
            class="form-input w-full mt-1">
        </label>

        <label class="block">
          <span class="form-label">Business type (structured data)</span>
          <select v-model="seo.schema_organization_type" class="form-select w-full mt-1">
            <option value="">Auto (LocalBusiness)</option>
            <option v-for="t in (seoDefaults.schema_org_types || [])" :key="t" :value="t">{{ t }}</option>
          </select>
          <span class="form-helper">Helps Google show rich results for your business.</span>
        </label>

        <label class="block">
          <span class="form-label">Logo URL (for rich results)</span>
          <input
            v-model="seo.schema_logo_url"
            type="text"
            placeholder="https://…/logo.png"
            class="form-input w-full mt-1">
        </label>

        <label class="flex items-center justify-between gap-2">
          <span class="text-sm text-gray-800">Allow search engines to index this site</span>
          <input v-model="seo.default_robots_index" type="checkbox" class="form-checkbox">
        </label>
        <p v-if="!seo.default_robots_index" class="text-[11px] text-amber-600">
          The entire site is hidden from search engines while this is off.
        </p>

        <label class="flex items-center justify-between gap-2">
          <span class="text-sm text-gray-800">Generate sitemap.xml</span>
          <input v-model="seo.sitemap_enabled" type="checkbox" class="form-checkbox">
        </label>

        <button
          type="submit"
          :disabled="saving"
          class="btn btn-primary btn-sm btn-full">
          {{ saving ? 'Saving…' : 'Save SEO settings' }}
        </button>
      </form>
    </details>
  </div>
</template>

<script setup>
import { computed, inject, reactive, ref } from 'vue'
import EditorIcon from './EditorIcon.vue'
import CityAutocomplete from './CityAutocomplete.vue'
import BusinessHoursGrid from './BusinessHoursGrid.vue'
import HeaderEditor from './HeaderEditor.vue'
import FooterEditor from './FooterEditor.vue'

const props = defineProps({
  pages: { type: Array, required: true },
  selectedPageId: { type: String, default: null },
  business: { type: Object, required: true },
  brandPalette: { type: Object, required: true },
  colorTokens: { type: Object, default: () => ({}) },
  blogEnabledInitial: { type: Boolean, default: false },
  blogPostsPath: { type: String, default: '' },
  chatInitial: { type: Object, default: () => ({}) },
  seoSettingsInitial: { type: Object, default: () => ({}) },
  headerInitial: { type: Object, default: () => ({}) },
  footerInitial: { type: Object, default: () => ({}) },
  seoDefaults: { type: Object, default: () => ({}) },
  googleMapsKey: { type: String, default: '' },
  saving: { type: Boolean, default: false },
  cloneableTemplatePages: { type: Array, default: () => []},
  entities: { type: Object, default: () => ({ services: [], service_areas: []}) }
})

const emit = defineEmits(['select-page', 'save-business', 'save-brand', 'save-blog', 'save-chat', 'save-header', 'save-footer', 'save-seo-settings', 'add-page', 'delete-page'])

const biz = reactive({ ...props.business })
const palette = reactive({ ...props.brandPalette })
const blogEnabled = ref(props.blogEnabledInitial)
const chat = reactive({ enabled: true, greeting: '', color: '', hide_branding: false, ...props.chatInitial })
const chatCustomColor = ref(!!props.chatInitial.color)
const defaultGreeting = 'Hi — ask us anything. We typically reply within an hour.'
const addForm = ref(null)

// Site-wide SEO defaults (sidebar SEO & Social panel).
const editorApi = inject('templateEditorApi', null)
const seo = reactive({
  description: '',
  default_og_image_url: '',
  schema_logo_url: '',
  schema_organization_type: '',
  twitter_handle: '',
  default_robots_index: true,
  default_robots_follow: true,
  sitemap_enabled: true,
  ...props.seoSettingsInitial
})
const seoImageUploading = ref(false)
const ogFileInput = ref(null)
function emitSeo() {
  emit('save-seo-settings', { ...seo })
}
async function uploadSeoImage(e) {
  const file = e.target.files && e.target.files[0]
  if (!file || !editorApi) return
  seoImageUploading.value = true
  try {
    const res = await editorApi.uploadImage(file, 'Default share image')
    if (res && res.url) { seo.default_og_image_url = res.url; emitSeo() }
  } finally {
    seoImageUploading.value = false
    e.target.value = ''
  }
}

// Build pageGroups: each entry is { kind, cloneable, templatePageId?, cloneEntity?, pages: [] }.
// Cloneable kinds collect all their stamped pages; singletons get exactly one.
const pageGroups = computed(() => {
  const cloneableByTemplateId = new Map(props.cloneableTemplatePages.map((t) => [t.id, t]))
  const groups = new Map()

  for (const page of props.pages) {
    const tp = cloneableByTemplateId.get(page.site_template_page_id)
    const key = tp ? `clone:${tp.id}` : `single:${page.kind}`
    if (!groups.has(key)) {
      groups.set(key, {
        kind: page.kind,
        cloneable: !!tp,
        templatePageId: tp?.id,
        cloneEntity: tp?.clone_entity,
        pages: []
      })
    }
    groups.get(key).pages.push(page)
  }

  // Also surface cloneable kinds that don't have any stamps yet so customers
  // can still hit "+ Add" — happens on a freshly-applied template before any
  // services/areas exist.
  for (const tp of props.cloneableTemplatePages) {
    const key = `clone:${tp.id}`
    if (groups.has(key)) continue
    groups.set(key, {
      kind: tp.kind,
      cloneable: true,
      templatePageId: tp.id,
      cloneEntity: tp.clone_entity,
      pages: []
    })
  }

  return Array.from(groups.values())
})

function isClone(page) {
  return Boolean(page.service_id || page.service_area_id)
}

function rowClass(_page) {
  // Layout, colors, hover + active state are all owned by the DS
  // `.sidebar-nav-item` (+ `.active`) class on the element. We only add the
  // utilities DS doesn't: full-width button + `group relative` so the
  // hover-reveal remove "×" works.
  return 'group relative w-full text-left'
}

function kindGroupLabel(kind) {
  return ({
    service_detail: 'Service pages',
    location: 'Location pages',
    service_location: 'Service × location pages'
  })[kind] || (KIND_LABELS[kind] || kind)
}

function openAddForm(group) {
  addForm.value = {
    kind: group.kind,
    templatePageId: group.templatePageId,
    needsService: group.cloneEntity === 'service' || group.cloneEntity === 'service_area_service',
    needsArea: group.cloneEntity === 'service_area' || group.cloneEntity === 'service_area_service',
    serviceId: '',
    serviceAreaId: '',
    error: null
  }
}

// Filter out entities that already have a page (so the dropdown doesn't
// let you double-add). For cross-pages, only filter when BOTH dimensions
// are pinned to an existing pair.
function availableServices(form) {
  const usedIds = new Set()
  props.pages.forEach((p) => {
    if (p.site_template_page_id === form.templatePageId && form.kind === 'service_detail' && p.service_id) {
      usedIds.add(p.service_id)
    }
  })
  return props.entities.services.filter((s) => !usedIds.has(s.id))
}

function availableAreas(form) {
  const usedIds = new Set()
  props.pages.forEach((p) => {
    if (p.site_template_page_id === form.templatePageId && form.kind === 'location' && p.service_area_id) {
      usedIds.add(p.service_area_id)
    }
  })
  return props.entities.service_areas.filter((a) => !usedIds.has(a.id))
}

const canSubmitAdd = computed(() => {
  if (!addForm.value) return false
  if (addForm.value.needsService && !addForm.value.serviceId) return false
  if (addForm.value.needsArea && !addForm.value.serviceAreaId) return false
  return true
})

function submitAdd() {
  if (!canSubmitAdd.value) return
  emit('add-page', {
    site_template_page_id: addForm.value.templatePageId,
    service_id: addForm.value.serviceId || null,
    service_area_id: addForm.value.serviceAreaId || null,
    onComplete: (result) => {
      if (result?.success === false) {
        addForm.value.error = result.error || 'Could not add page'
      } else {
        addForm.value = null
      }
    }
  })
}

function emitBusiness() {
  emit('save-business', { ...biz })
}

// Picking a city from Places autocomplete fills the city and, when available,
// the state — the customer still confirms with "Save business info".
function onCitySelect({ city, state }) {
  if (city) biz.city = city
  if (state) biz.state = state
}
function emitBrand() {
  emit('save-brand', { ...palette })
}
function emitBlog() {
  emit('save-blog', { enabled: blogEnabled.value })
}
function emitChat() {
  emit('save-chat', {
    enabled: chat.enabled,
    greeting: chat.greeting || '',
    hide_branding: !!chat.hide_branding,
    // Empty color tells the server to fall back to the brand primary.
    color: chatCustomColor.value ? (chat.color || chat.primary_default || '') : ''
  })
}

// Friendly fallback names per page kind so the rail never renders a
// blank row when title is missing in the manifest.
const KIND_LABELS = {
  home: 'Home',
  about: 'About',
  services_overview: 'Services',
  service_detail: 'Service detail',
  service_areas: 'Service areas',
  location: 'Service area',
  service_location: 'Service in area',
  contact: 'Contact',
  faq: 'FAQ',
  blog: 'Blog',
  legal: 'Legal'
}

function labelFor(page) {
  return (page.title && page.title.trim()) || KIND_LABELS[page.kind] || (page.kind || 'Page').replace(/_/g, ' ')
}

// Tiny inline SVGs (heroicons-flavored, 1.5px stroke, currentColor).
const KIND_ICONS = {
  home: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="m3 11 9-7 9 7v9a1 1 0 0 1-1 1h-5v-6h-6v6H4a1 1 0 0 1-1-1z"/></svg>',
  about: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="8" r="3.5"/><path d="M4.5 20a7.5 7.5 0 0 1 15 0"/></svg>',
  services_overview: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>',
  service_detail: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="4" y="4" width="16" height="16" rx="2"/><path d="M8 9h8M8 13h8M8 17h5"/></svg>',
  service_areas: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3a13.5 13.5 0 0 1 0 18M12 3a13.5 13.5 0 0 0 0 18"/></svg>',
  location: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M12 22s7-7.5 7-12.5a7 7 0 1 0-14 0C5 14.5 12 22 12 22z"/><circle cx="12" cy="10" r="2.5"/></svg>',
  service_location: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M12 22s7-7.5 7-12.5a7 7 0 1 0-14 0C5 14.5 12 22 12 22z"/><path d="M9 9h6M9 11.5h4"/></svg>',
  contact: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M4 6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z"/><path d="m4 7 8 6 8-6"/></svg>',
  faq: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="9"/><path d="M9.5 9a2.5 2.5 0 0 1 5 0c0 1.5-2.5 2-2.5 4"/><circle cx="12" cy="17" r=".75" fill="currentColor"/></svg>',
  blog: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M4 5a2 2 0 0 1 2-2h10l4 4v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z"/><path d="M8 13h8M8 17h6"/></svg>',
  legal: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M12 3v18M5 7l-2 5h4zM19 7l-2 5h4zM3 12a2 2 0 0 0 2 2 2 2 0 0 0 2-2M17 12a2 2 0 0 0 2 2 2 2 0 0 0 2-2M8 21h8"/></svg>'
}

const FALLBACK_ICON = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="4" y="4" width="16" height="16" rx="2"/></svg>'

function iconFor(kind) {
  return KIND_ICONS[kind] || FALLBACK_ICON
}
</script>
